import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStyles } from './styles';
import { Fade, Grid2 } from '@mui/material';
import Card from '../Card';
import DonutChart from '../DonutChart';
import BarChart from '../BarChart';
import Container from '../Container';
import { useReport } from 'core/contexts/reports';
import { CircularIndeterminate } from '@utriper/core-ui';
import { useCurrency } from 'core/contexts/currency';

const AppReport = () => {
    const { t: translate } = useTranslation();
    const classes = useStyles();
    const { loadingApp, appReports, dayLabels, monthsMap, getValuesByOrigin, getValues, getDateValues, getMonthlyValues, getTotalsApp } = useReport();
    const { currency, currencies } = useCurrency();

    // TOTALS
    const reportTotals = useMemo(() => {
        const auxTotals = getTotalsApp(appReports?.totals, appReports?.rates);
        return auxTotals ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appReports?.totals, appReports?.rates, currencies, currency]);

    // BY ORIGIN
    const ticketsSoldByOrigin = useMemo(() => {
        const auxTicketsSold = getValuesByOrigin(appReports?.byOrigin, 'ticketsSold', appReports?.rates);
        return auxTicketsSold ?? [];
    }, [appReports?.byOrigin, appReports?.rates, currencies, currency]);

    const totalPriceByOrigin = useMemo(() => {
        const auxTotalPrice = getValuesByOrigin(appReports?.byOrigin, 'totalPrice', appReports?.rates);
        return auxTotalPrice ?? [];
    }, [appReports?.byOrigin, appReports?.rates, currencies, currency]);

    const hostCommissionByOrigin = useMemo(() => {
        const auxHostCommissions = getValuesByOrigin(appReports?.byOrigin, 'hostCommission', appReports?.rates);
        return auxHostCommissions ?? [];
    }, [appReports?.byOrigin, appReports?.rates, currencies, currency]);

    const utriperCommissionByOrigin = useMemo(() => {
        const auxUtriperCommissions = getValuesByOrigin(appReports?.byOrigin, 'utriperCommission', appReports?.rates);
        return auxUtriperCommissions ?? [];
    }, [appReports?.byOrigin, appReports?.rates, currencies, currency]);

    // BY EXPERIENCE
    const ticketsSoldByExperience = useMemo(() => {
        const auxTicketsSold = getValues(appReports?.byExperience, 'ticketsSold', appReports?.rates);
        return auxTicketsSold ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appReports?.byExperience, appReports?.rates, currencies, currency]);

    const totalPriceByExperience = useMemo(() => {
        const auxTotalPrice = getValues(appReports?.byExperience, 'totalPrice', appReports?.rates);
        return auxTotalPrice ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appReports?.byExperience, appReports?.rates, currencies, currency]);

    // BY HOST AGENT
    const ticketsSoldByHostAgent = useMemo(() => {
        const auxTicketsSold = getValues(appReports?.byHostAgent, 'ticketsSold', appReports?.rates);
        return auxTicketsSold ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appReports?.byHostAgent, appReports?.rates, currencies, currency]);

    const totalPriceByHostAgent = useMemo(() => {
        const auxTotalPrice = getValues(appReports?.byHostAgent, 'totalPrice', appReports?.rates);
        return auxTotalPrice ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appReports?.byHostAgent, appReports?.rates, currencies, currency]);

    // EXPERIENCE DATES
    const experienceDates = useMemo(() => {
        const auxSeries = getDateValues(appReports?.experienceDateDistribution);
        return auxSeries ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appReports?.experienceDateDistribution]);

    // BOOKING DATES
    const bookingDates = useMemo(() => {
        const auxSeries = getDateValues(appReports?.bookingDateDistribution);
        return auxSeries ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appReports?.bookingDateDistribution]);

    // BOOKING EVOLUTION
    const bookingEvolution = useMemo(() => {
        const auxSeries = getMonthlyValues(appReports?.bookingMonthlyEvolution);
        return auxSeries ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appReports?.bookingMonthlyEvolution]);

    return loadingApp ? (
        <div style={{ marginTop: 2 }}>
            <CircularIndeterminate />
        </div>
    ) : (
        <Fade in={!loadingApp}>
            <Grid2 container size={12} direction="column" width="100%" justifyContent="flex-start" alignItems="flex-start" gap="16px" pb={4}>
                {/* Cards Totals */}
                <Grid2 container size={{ xs: 6, md: 12 }} gap="8px" justifyContent="flex-start" alignItems="center" width="100%" flexWrap={{ xs: 'wrap', lg: 'nowrap' }}>
                    {reportTotals?.map(({ title, value, currency = null }) => (
                        <Card key={title} title={title} value={value} currency={currency} />
                    ))}
                </Grid2>

                {/* Charts By Origin */}
                <Container>
                    {/* Tickets Sold */}
                    {ticketsSoldByOrigin?.length > 0 && (
                        <Grid2 size={{ xs: 2, md: 4 }} className={classes.chartContainer}>
                            <DonutChart title={translate('reports.app_report.tickets_sold_by_origin')} subtitle="Tickets" series={ticketsSoldByOrigin?.map((ticket) => ticket?.value)} optionLabels={ticketsSoldByOrigin?.map((ticket) => ticket?.name)} />
                        </Grid2>
                    )}

                    {/* Total Price */}
                    {totalPriceByOrigin?.length > 0 && (
                        <Grid2 size={{ xs: 2, md: 4 }} className={classes.chartContainer}>
                            <DonutChart title={translate('reports.app_report.total_price_by_origin')} subtitle={currency} series={totalPriceByOrigin?.map((ticket) => ticket?.value)} optionLabels={totalPriceByOrigin?.map((ticket) => ticket?.name)} />
                        </Grid2>
                    )}
                </Container>

                {/* Commission Charts By Origin */}
                <Container>
                    {/* Host Commission */}
                    {hostCommissionByOrigin?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.app_report.host_commission_by_origin')}
                                subtitle={currency}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.app_report.host_commission'),
                                        data: hostCommissionByOrigin?.map((hostCommission) => hostCommission?.value),
                                    },
                                ]}
                                categories={hostCommissionByOrigin?.map((hostCommission) => hostCommission?.name)}
                                horizontal
                            />
                        </Grid2>
                    )}

                    {/* uTriper Commission */}
                    {utriperCommissionByOrigin?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.app_report.utriper_commission_by_origin')}
                                subtitle={currency}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.app_report.utriper_commission'),
                                        data: utriperCommissionByOrigin?.map((utriperCommission) => utriperCommission?.value),
                                    },
                                ]}
                                categories={utriperCommissionByOrigin?.map((utriperCommission) => utriperCommission?.name)}
                                horizontal
                            />
                        </Grid2>
                    )}
                </Container>

                {/* Charts By Experience */}
                <Container>
                    {/* Tickets Sold */}
                    {ticketsSoldByExperience?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.app_report.tickets_sold_by_experience')}
                                subtitle={'Tickets'}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.tickets_sold'),
                                        data: ticketsSoldByExperience?.map((ticket) => ticket?.value),
                                    },
                                ]}
                                categories={ticketsSoldByExperience?.map((ticket) => ticket?.name)}
                            />
                        </Grid2>
                    )}

                    {/* Total Price */}
                    {totalPriceByExperience?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.app_report.total_price_by_experience')}
                                subtitle={currency}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.price_sold'),
                                        data: totalPriceByExperience?.map((totalPrice) => totalPrice?.value),
                                    },
                                ]}
                                categories={totalPriceByExperience?.map((totalPrice) => totalPrice?.name)}
                            />
                        </Grid2>
                    )}
                </Container>

                {/* Charts By Host Agent */}
                <Container>
                    {/* Tickets Sold */}
                    {ticketsSoldByHostAgent?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.app_report.tickets_sold_by_host_agent')}
                                subtitle={'Tickets'}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.tickets_sold'),
                                        data: ticketsSoldByHostAgent?.map((ticket) => ticket?.value),
                                    },
                                ]}
                                categories={ticketsSoldByHostAgent?.map((ticket) => ticket?.name)}
                            />
                        </Grid2>
                    )}

                    {/* Total Price */}
                    {totalPriceByHostAgent?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.app_report.total_price_by_host_agent')}
                                subtitle={currency}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.price_sold'),
                                        data: totalPriceByHostAgent?.map((totalPrice) => totalPrice?.value),
                                    },
                                ]}
                                categories={totalPriceByHostAgent?.map((totalPrice) => totalPrice?.name)}
                            />
                        </Grid2>
                    )}
                </Container>

                {/* Evolution Charts */}
                <Container>
                    {/* Experience Date Distribution */}
                    {experienceDates?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart title={translate('reports.experience_dates')} subtitle={currency} label={'Tickets'} series={experienceDates} categories={dayLabels} horizontal />
                        </Grid2>
                    )}

                    {/* Booking Monthly Evolution */}
                    {bookingEvolution?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.booking_evolution')}
                                subtitle={currency}
                                series={[
                                    {
                                        name: translate('reports.sales'),
                                        data: bookingEvolution.map((bkg) => appReports?.bookingMonthlyEvolution[bkg]),
                                    },
                                ]}
                                categories={bookingEvolution?.map((bkg) => monthsMap[bkg])}
                            />
                        </Grid2>
                    )}
                    {/* Booking Date Distribution */}
                    {bookingDates?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart title={translate('reports.booking_dates')} subtitle={currency} label={'Tickets'} series={bookingDates} categories={dayLabels} horizontal />
                        </Grid2>
                    )}
                </Container>
            </Grid2>
        </Fade>
    );
};

export default AppReport;
